import { useLightMode } from '@/hooks/useLightMode'
import { usePermissions } from '@/hooks/usePermissions'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Box as DarkBox,
  Modal as DarkModal,
  Text as DarkText,
} from '@hivewatch/dark'

import { IncidentActionType } from '@/graphql/generated/schemas'
import { tVar } from '@/utils/i18n'

interface IProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: () => void
  incidentName: string
  currentOwner: string
  displayId: string
  isLoading: boolean
  nextAction:
    | IncidentActionType.AcknowledgementByOperator
    | IncidentActionType.Resolved
}

const getIncidentActionDisplayName = (
  action: IncidentActionType,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: TFunction<any>
) => {
  switch (action) {
    case IncidentActionType.AcknowledgementByOperator:
      return t('incidents:acknowledge')
    case IncidentActionType.Resolved:
      return t('incidents:incident_drawer_resolve_header')
  }
}

export const TakeOverIncidentModal = ({
  isOpen,
  onClose,
  onSubmit,
  incidentName,
  currentOwner,
  displayId,
  isLoading,
  nextAction,
}: IProps) => {
  const { t } = useTranslation()
  const { shouldEnableIncidentActions } = usePermissions()
  const colorScheme = useLightMode()

  const actionName = getIncidentActionDisplayName(nextAction, t)
  return (
    <DarkModal
      bodyStyleOverrides={{ pb: '8px' }}
      colorScheme={colorScheme}
      isOpen={isOpen}
      isSaveDestructive
      isSaveDisabled={!shouldEnableIncidentActions}
      isSaveLoading={isLoading}
      onClose={onClose}
      onSave={onSubmit}
      saveButtonLabel={tVar(t, 'incidents:incident_drawer_take_over_button', {
        action: actionName,
      })}
      showCancelButton
      size='compact'
      title={t('incidents:incident_drawer_take_over_title')}
      zIndex={1500}
    >
      <DarkText
        color={
          colorScheme === 'light' ? 'light.text.secondary' : 'text.secondary'
        }
        textStyle='caption'
      >
        {t('incidents:incident_drawer_release_incident_name_label')}
      </DarkText>
      <DarkText
        color={colorScheme === 'light' ? 'light.text.primary' : 'text.primary'}
        data-testid='TakeOverIncidentModal:incidentDisplayName'
        mt='4px'
        textStyle='heading'
      >
        {`${displayId} ${incidentName}`}
      </DarkText>
      <DarkBox mt='16px'>
        <Alert
          colorScheme={colorScheme}
          description={tVar(t, 'incidents:incident_drawer_take_over_warning', {
            owner: currentOwner,
            action: actionName,
          })}
          title={t('common:warning_title')}
          variant='error'
        />
      </DarkBox>
    </DarkModal>
  )
}
